import React, { useState, useEffect, useCallback } from "react";
import {
  CreditCard,
  Check,
  X,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  FileText,
  Download,
  ArrowRight,
  Zap,
  Database,
  Brain,
  Server,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { AUTH_URL } from "../../constants";

const SERVICE_ICONS = { rag: Database, slm: Brain, private_llm: Server };

const StatusBadge = ({ status }) => {
  const colors = {
    active: "bg-green-100 text-green-700",
    paid: "bg-green-100 text-green-700",
    cancelled: "bg-red-100 text-red-700",
    suspended: "bg-yellow-100 text-yellow-700",
    failed: "bg-red-100 text-red-700",
    unpaid: "bg-slate-100 text-slate-600",
    pending: "bg-yellow-100 text-yellow-700",
    draft: "bg-slate-100 text-slate-600",
  };
  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || "bg-slate-100 text-slate-600"}`}
    >
      {status}
    </span>
  );
};

const PlanCard = ({
  product,
  isCurrentPlan,
  billingCycle,
  onSelect,
  loading,
}) => {
  const price = product.base_price || 0;
  const annualPrice = Math.round(price * 10.8) / 12; // 10% annual discount
  const displayPrice = billingCycle === "annual" ? annualPrice : price;
  const services = [];
  if (product.includes_rag) services.push("RAG");
  if (product.includes_slm) services.push("SLM");
  if (product.includes_private_llm) services.push("Private LLM");

  return (
    <div
      className={`rounded-xl border-2 p-6 transition-all ${
        isCurrentPlan
          ? "border-blue-500 bg-blue-50/50 shadow-md"
          : "border-slate-200 hover:border-blue-300 hover:shadow-sm"
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-slate-900">
          {product.product_name}
        </h3>
        {isCurrentPlan && (
          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
            Current Plan
          </span>
        )}
      </div>

      <p className="text-sm text-slate-500 mb-4 min-h-[40px]">
        {product.description || "AI-powered knowledge base solution"}
      </p>

      <div className="mb-4">
        <span className="text-3xl font-bold text-slate-900">
          ${displayPrice.toFixed(0)}
        </span>
        <span className="text-slate-500 text-sm">/month</span>
        {billingCycle === "annual" && (
          <p className="text-xs text-green-600 mt-1">
            Save 10% with annual billing (${(price * 12).toFixed(0)} $
            {(annualPrice * 12).toFixed(0)}/yr)
          </p>
        )}
      </div>

      <div className="space-y-2 mb-6">
        {services.map((s) => {
          const Icon = SERVICE_ICONS[s.toLowerCase().replace(" ", "_")] || Zap;
          return (
            <div key={s} className="flex items-center text-sm text-slate-700">
              <Icon className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" />
              {s} Service
            </div>
          );
        })}
      </div>

      {!isCurrentPlan && (
        <button
          onClick={() => onSelect(product)}
          disabled={loading}
          className="w-full py-2.5 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-medium flex items-center justify-center"
        >
          {loading ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <>
              Subscribe <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </button>
      )}

      {isCurrentPlan && (
        <div className="w-full py-2.5 px-4 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium text-center">
          <Check className="w-4 h-4 inline mr-1" />
          Active
        </div>
      )}
    </div>
  );
};

export default function BillingManagement() {
  const { token } = useAuth();
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState("");
  const [products, setProducts] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [usage, setUsage] = useState({});
  const [usageLoading, setUsageLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchFacilities = useCallback(async () => {
    try {
      const res = await fetch(`${AUTH_URL}/facilities`, { headers });
      const data = await res.json();
      if (Array.isArray(data)) {
        setFacilities(data);
        if (data.length > 0 && !selectedFacility) {
          setSelectedFacility(data[0].facility_id);
        }
      }
    } catch (e) {
      console.error("Failed to fetch facilities:", e);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProducts = useCallback(async () => {
    try {
      const res = await fetch(`${AUTH_URL}/billing/products`, { headers });
      const data = await res.json();
      if (Array.isArray(data)) setProducts(data);
    } catch (e) {
      console.error("Failed to fetch products:", e);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSubscription = useCallback(async () => {
    if (!selectedFacility) return;
    try {
      const res = await fetch(
        `${AUTH_URL}/billing/subscription?facility_id=${encodeURIComponent(selectedFacility)}`,
        { headers },
      );
      const data = await res.json();
      if (!data.error) setSubscription(data);
      else setSubscription(null);
    } catch {
      setSubscription(null);
    }
  }, [selectedFacility, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInvoices = useCallback(async () => {
    if (!selectedFacility) return;
    try {
      const res = await fetch(
        `${AUTH_URL}/billing/invoices?facility_id=${encodeURIComponent(selectedFacility)}`,
        { headers },
      );
      const data = await res.json();
      if (Array.isArray(data)) setInvoices(data);
    } catch {
      setInvoices([]);
    }
  }, [selectedFacility, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsage = useCallback(async () => {
    if (!selectedFacility) return;
    setUsageLoading(true);
    try {
      const res = await fetch(
        `${AUTH_URL}/billing/usage?facility_id=${encodeURIComponent(selectedFacility)}&period=30d`,
        { headers },
      );
      const data = await res.json();
      setUsage(data.error ? {} : data);
    } catch {
      setUsage({});
    } finally {
      setUsageLoading(false);
    }
  }, [selectedFacility, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchFacilities(), fetchProducts()]).finally(() =>
      setLoading(false),
    );
  }, [fetchFacilities, fetchProducts]);

  useEffect(() => {
    if (selectedFacility) {
      fetchSubscription();
      fetchInvoices();
      fetchUsage();
    }
  }, [selectedFacility, fetchSubscription, fetchInvoices, fetchUsage]);

  const handleCheckout = async (product) => {
    setCheckoutLoading(true);
    setError("");
    try {
      const res = await fetch(`${AUTH_URL}/billing/create-checkout`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          facility_id: selectedFacility,
          product_code: product.product_code,
          billing_cycle: billingCycle,
        }),
      });
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError(data.error || "Failed to create checkout session");
      }
    } catch (e) {
      setError("Failed to initiate checkout");
    }
    setCheckoutLoading(false);
  };

  const handlePortal = async () => {
    try {
      const res = await fetch(`${AUTH_URL}/billing/portal`, {
        method: "POST",
        headers,
        body: JSON.stringify({ facility_id: selectedFacility }),
      });
      const data = await res.json();
      if (data.portal_url) {
        window.open(data.portal_url, "_blank");
      } else {
        setError(data.error || "Failed to open billing portal");
      }
    } catch {
      setError("Failed to open billing portal");
    }
  };

  const handleCancel = async () => {
    if (
      !window.confirm(
        "Cancel subscription? It will remain active until the end of the current billing period.",
      )
    )
      return;
    try {
      const res = await fetch(`${AUTH_URL}/billing/cancel`, {
        method: "POST",
        headers,
        body: JSON.stringify({ facility_id: selectedFacility }),
      });
      const data = await res.json();
      if (data.success) {
        fetchSubscription();
      } else {
        setError(data.error || "Failed to cancel subscription");
      }
    } catch {
      setError("Failed to cancel subscription");
    }
  };

  const calcCost = (tokensIn, tokensOut) => {
    const inCost = ((tokensIn || 0) * 0.00015) / 1000;
    const outCost = ((tokensOut || 0) * 0.0006) / 1000;
    return (inCost + outCost).toFixed(2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-3 text-slate-500">Loading billing...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
          <span className="text-red-700 text-sm">{error}</span>
          <button
            onClick={() => setError("")}
            className="ml-auto text-red-400 hover:text-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Facility Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Billing & Subscriptions
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Manage plans, payments, and invoices for your facilities
            </p>
          </div>
          <select
            value={selectedFacility}
            onChange={(e) => setSelectedFacility(e.target.value)}
            className="px-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {facilities.map((f) => (
              <option key={f.facility_id} value={f.facility_id}>
                {f.facility_name || f.display_name || f.facility_id}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Current Subscription Status */}
      {subscription && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">
              Current Subscription
            </h3>
            <div className="flex items-center gap-3">
              <StatusBadge
                status={subscription.subscription_status || "none"}
              />
              {subscription.stripe_customer_id && (
                <button
                  onClick={handlePortal}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
                >
                  Manage in Stripe <ExternalLink className="w-3.5 h-3.5 ml-1" />
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Plan</p>
              <p className="text-sm font-semibold text-slate-900">
                {subscription.product_name || "No active plan"}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Monthly Price</p>
              <p className="text-sm font-semibold text-slate-900">
                {subscription.monthly_price
                  ? `$${Number(subscription.monthly_price).toFixed(2)}`
                  : "--"}
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Payment Status</p>
              <StatusBadge
                status={subscription.stripe_payment_status || "unpaid"}
              />
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-xs text-slate-500 mb-1">Billing Period</p>
              <p className="text-sm text-slate-700">
                {subscription.billing_start
                  ? new Date(subscription.billing_start).toLocaleDateString()
                  : "--"}{" "}
                &mdash;{" "}
                {subscription.billing_end
                  ? new Date(subscription.billing_end).toLocaleDateString()
                  : "Ongoing"}
              </p>
            </div>
          </div>

          {subscription.subscription_status === "active" && (
            <div className="mt-4 pt-4 border-t border-slate-100 flex justify-end">
              <button
                onClick={handleCancel}
                className="text-sm text-red-600 hover:text-red-700 hover:underline"
              >
                Cancel Subscription
              </button>
            </div>
          )}
        </div>
      )}

      {/* Usage Metering — always shown; values default to 0 when not yet tracked */}
      {selectedFacility && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">
              Usage — Last 30 Days
            </h3>
            {usageLoading && (
              <RefreshCw className="w-4 h-4 animate-spin text-slate-400" />
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              {
                label: "Conversations",
                value: (usage?.total_conversations || 0).toLocaleString(),
                color: "text-slate-900",
              },
              {
                label: "Sessions",
                value: (usage?.unique_sessions || 0).toLocaleString(),
                color: "text-slate-900",
              },
              {
                label: "Tokens In",
                value: (usage?.total_tokens_in || 0).toLocaleString(),
                color: "text-slate-900",
              },
              {
                label: "Tokens Out",
                value: (usage?.total_tokens_out || 0).toLocaleString(),
                color: "text-slate-900",
              },
              {
                label: "Est. LLM Cost",
                value: `$${calcCost(usage?.total_tokens_in, usage?.total_tokens_out)}`,
                color: "text-blue-600",
              },
            ].map((stat) => (
              <div key={stat.label} className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-500 mb-1">{stat.label}</p>
                {usageLoading ? (
                  <div className="h-7 w-16 bg-slate-200 animate-pulse rounded mt-1" />
                ) : (
                  <p className={`text-xl font-bold ${stat.color}`}>
                    {stat.value}
                  </p>
                )}
              </div>
            ))}
          </div>
          {!usageLoading &&
            (usage?.total_tokens_in || 0) === 0 &&
            (usage?.total_conversations || 0) === 0 && (
              <p className="text-xs text-slate-400 mt-3">
                No conversation data recorded yet for this facility in the last
                30 days.
              </p>
            )}
        </div>
      )}

      {/* Plan Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-900">
            Available Plans
          </h3>
          <div className="flex items-center bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                billingCycle === "monthly"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("annual")}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                billingCycle === "annual"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Annual
              <span className="ml-1 text-xs text-green-600">-10%</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {products.map((p) => (
            <PlanCard
              key={p.product_code}
              product={p}
              isCurrentPlan={
                subscription?.product_code === p.product_code &&
                subscription?.subscription_status === "active"
              }
              billingCycle={billingCycle}
              onSelect={handleCheckout}
              loading={checkoutLoading}
            />
          ))}
        </div>
      </div>

      {/* Invoice History */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Invoice History
          </h3>
          <button
            onClick={fetchInvoices}
            className="text-sm text-slate-500 hover:text-slate-700 flex items-center"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
            Refresh
          </button>
        </div>

        {invoices.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <FileText className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">No invoices yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-2 text-slate-500 font-medium">
                    Date
                  </th>
                  <th className="text-left py-3 px-2 text-slate-500 font-medium">
                    Invoice
                  </th>
                  <th className="text-left py-3 px-2 text-slate-500 font-medium">
                    Period
                  </th>
                  <th className="text-right py-3 px-2 text-slate-500 font-medium">
                    Amount
                  </th>
                  <th className="text-center py-3 px-2 text-slate-500 font-medium">
                    Status
                  </th>
                  <th className="text-right py-3 px-2 text-slate-500 font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr
                    key={inv.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                  >
                    <td className="py-3 px-2 text-slate-700">
                      {inv.created_at
                        ? new Date(inv.created_at).toLocaleDateString()
                        : "--"}
                    </td>
                    <td className="py-3 px-2 text-slate-600 font-mono text-xs">
                      {inv.stripe_invoice_id
                        ? inv.stripe_invoice_id.slice(0, 20)
                        : `INV-${inv.id}`}
                    </td>
                    <td className="py-3 px-2 text-slate-600">
                      {inv.period_start
                        ? new Date(inv.period_start).toLocaleDateString()
                        : "--"}{" "}
                      &ndash;{" "}
                      {inv.period_end
                        ? new Date(inv.period_end).toLocaleDateString()
                        : "--"}
                    </td>
                    <td className="py-3 px-2 text-right font-medium text-slate-900">
                      ${((inv.amount_cents || 0) / 100).toFixed(2)}
                    </td>
                    <td className="py-3 px-2 text-center">
                      <StatusBadge status={inv.status} />
                    </td>
                    <td className="py-3 px-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {inv.hosted_invoice_url && (
                          <a
                            href={inv.hosted_invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700"
                            title="View invoice"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        {inv.pdf_url && (
                          <a
                            href={inv.pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate-500 hover:text-slate-700"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
